import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Button,
  IconButton,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import DataGrid, {
  Column,
  Paging,
  Sorting,
  LoadPanel,
  ColumnFixing
} from "devextreme-react/data-grid";
import { TextBox } from "devextreme-react/text-box";
import { NumberBox } from "devextreme-react/number-box";
import { TextArea } from "devextreme-react/text-area";
import AdminDialog from "./AdminDialog";
import {
  getCustomerPricings,
  createCustomerPricing,
  editCustomerPricing,
  deleteCustomerPricing
} from "../../redux/customerPricing/thunks";
import { getCustomerServices } from "../../redux/customerServices/thunks";
import {
  selectCustomerPricingsByCustomer,
  selectCustomerPricingLoading
} from "../../redux/customerPricing/selectors";
import { openModal } from "../../redux/modal/modalSlice";
import editIcon from "../../images/edit.svg";
import deleteIcon from "../../images/delete.svg";
import AddIcon from "@material-ui/icons/Add";

const useStyles = makeStyles({
  wrapper: {
    padding: "0.75rem 1rem 1rem 1rem",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 0,
    margin: "0.5rem 0",
    width: "100%",
    boxSizing: "border-box"
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.5rem",
    marginBottom: "0.75rem",
    flexWrap: "wrap"
  },
  title: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#334155"
  },
  dialogContentContainer: {
    maxHeight: "65vh",
    overflowY: "auto",
    paddingRight: "0.25rem"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.75rem",
    padding: "0.25rem 0"
  },
  fullWidth: {
    gridColumn: "1 / -1"
  },
  fixedColumn: {
    backgroundColor: "#EFF1F2 !important"
  },
  specialtyCell: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    width: "100%"
  },
  actionsCell: {
    display: "flex",
    gap: "0.25rem",
    justifyContent: "center"
  },
  deleteConfirmText: {
    fontSize: "0.95rem",
    padding: "1rem 0"
  }
});

const formatRateValue = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed || trimmed === "-") return "-";
    if (trimmed.startsWith("R$")) return trimmed;
    const cleanNum = Number(trimmed.replace(",", "."));
    if (!isNaN(cleanNum)) {
      return `R$ ${cleanNum.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return trimmed;
  }
  if (typeof val === "number") {
    return `R$ ${val.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return String(val);
};

const initialFormData = {
  specialtyName: "",
  billingModel: "DEMANDA",
  reportDescription: "",
  kmAllowance: "",
  baseRate: null,
  excessKmRate: null,
  specialKmRate: null,
  dirtRoadKmRate: null,
  skatesRate: null,
  wheelRate: null,
  stationDailyRate: null,
  towingUpRate: null,
  subwayRetentionRate: null,
  offroadRate: null,
  stopHourRate: null,
  workHourRate: null,
  softwarePerPlate: null,
  toll: "",
  concierge: "",
  goLive: "",
  observations: "",
  closingObservations: ""
};

function CustomerSpecialtiesDetail(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const service = props.service || props.data?.data || props.data || {};
  const customerId = props.customerId || service.customerId;
  const serviceTypeId = service.supplierServiceTypeId;
  const serviceTypeName = service.serviceTypeName || service.description || "";
  const currentBillingModel = props.billingModel || service.billingModel || "DEMANDA";

  const allPricings = useSelector((state) =>
    selectCustomerPricingsByCustomer(state, customerId)
  );
  const loading = useSelector(selectCustomerPricingLoading);

  const specialtiesFromProps = props.specialties || service.specialties;
  const pricings = (specialtiesFromProps && specialtiesFromProps.length > 0)
    ? specialtiesFromProps
    : (allPricings || []).filter(
        (p) =>
          Number(p.supplierServiceTypeId) === Number(serviceTypeId) &&
          (!currentBillingModel || !p.billingModel || p.billingModel.toUpperCase() === currentBillingModel.toUpperCase())
      );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [operation, setOperation] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [formData, setFormData] = useState({
    ...initialFormData,
    billingModel: currentBillingModel
  });

  const handleOpenCreate = () => {
    setOperation("create");
    setSelectedPricing(null);
    setFormData({
      ...initialFormData,
      billingModel: currentBillingModel
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (pricing) => {
    setOperation("edit");
    setSelectedPricing(pricing);
    setFormData({
      specialtyName: pricing.specialtyName || "",
      billingModel: pricing.billingModel || currentBillingModel,
      reportDescription: pricing.reportDescription || "",
      kmAllowance: pricing.kmAllowance || "",
      baseRate: pricing.baseRate,
      excessKmRate: pricing.excessKmRate,
      specialKmRate: pricing.specialKmRate,
      dirtRoadKmRate: pricing.dirtRoadKmRate,
      skatesRate: pricing.skatesRate,
      wheelRate: pricing.wheelRate,
      stationDailyRate: pricing.stationDailyRate,
      towingUpRate: pricing.towingUpRate,
      subwayRetentionRate: pricing.subwayRetentionRate,
      offroadRate: pricing.offroadRate,
      stopHourRate: pricing.stopHourRate,
      workHourRate: pricing.workHourRate,
      softwarePerPlate: pricing.softwarePerPlate,
      toll: pricing.toll || "",
      concierge: pricing.concierge || "",
      goLive: pricing.goLive || "",
      observations: pricing.observations || "",
      closingObservations: pricing.closingObservations || ""
    });
    setDialogOpen(true);
  };

  const handleOpenDelete = (pricing) => {
    setOperation("delete");
    setSelectedPricing(pricing);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setOperation(null);
    setSelectedPricing(null);
    setFormData({
      ...initialFormData,
      billingModel: currentBillingModel
    });
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    if (operation === "delete") {
      dispatch(
        deleteCustomerPricing({ param: selectedPricing.customerPricingId })
      ).then((res) => {
        if (res.error) {
          dispatch(openModal({ type: "warning", message: res.error.message }));
        } else {
          dispatch(getCustomerPricings({ param: customerId }));
          dispatch(getCustomerServices({ param: customerId }));
          handleCloseDialog();
        }
      });
      return;
    }

    if (!formData.specialtyName?.trim()) {
      dispatch(openModal({ type: "warning", message: t("PleaseFillAllFields") }));
      return;
    }

    const payload = {
      customerId: customerId,
      supplierServiceTypeId: Number(serviceTypeId),
      billingModel: formData.billingModel || currentBillingModel,
      specialtyName: formData.specialtyName.trim(),
      reportDescription: formData.reportDescription?.trim() || null,
      kmAllowance: formData.kmAllowance?.trim() || null,
      baseRate: formData.baseRate !== null && formData.baseRate !== "" ? String(formData.baseRate) : null,
      excessKmRate: formData.excessKmRate !== null && formData.excessKmRate !== "" ? String(formData.excessKmRate) : null,
      specialKmRate: formData.specialKmRate !== null && formData.specialKmRate !== "" ? String(formData.specialKmRate) : null,
      dirtRoadKmRate: formData.dirtRoadKmRate !== null && formData.dirtRoadKmRate !== "" ? String(formData.dirtRoadKmRate) : null,
      skatesRate: formData.skatesRate !== null && formData.skatesRate !== "" ? String(formData.skatesRate) : null,
      wheelRate: formData.wheelRate !== null && formData.wheelRate !== "" ? String(formData.wheelRate) : null,
      stationDailyRate: formData.stationDailyRate !== null && formData.stationDailyRate !== "" ? String(formData.stationDailyRate) : null,
      towingUpRate: formData.towingUpRate !== null && formData.towingUpRate !== "" ? String(formData.towingUpRate) : null,
      subwayRetentionRate: formData.subwayRetentionRate !== null && formData.subwayRetentionRate !== "" ? String(formData.subwayRetentionRate) : null,
      offroadRate: formData.offroadRate !== null && formData.offroadRate !== "" ? String(formData.offroadRate) : null,
      stopHourRate: formData.stopHourRate !== null && formData.stopHourRate !== "" ? String(formData.stopHourRate) : null,
      workHourRate: formData.workHourRate !== null && formData.workHourRate !== "" ? String(formData.workHourRate) : null,
      softwarePerPlate: formData.softwarePerPlate !== null && formData.softwarePerPlate !== "" ? String(formData.softwarePerPlate) : null,
      toll: formData.toll?.trim() || null,
      concierge: formData.concierge?.trim() || null,
      goLive: formData.goLive?.trim() || null,
      observations: formData.observations?.trim() || null,
      closingObservations: formData.closingObservations?.trim() || null
    };

    if (operation === "create") {
      dispatch(createCustomerPricing({ body: payload })).then((res) => {
        if (res.error) {
          dispatch(
            openModal({
              type: "warning",
              message: res.error.message || t("PricingAlreadyExists")
            })
          );
        } else {
          dispatch(getCustomerPricings({ param: customerId }));
          dispatch(getCustomerServices({ param: customerId }));
          handleCloseDialog();
        }
      });
    } else if (operation === "edit") {
      dispatch(
        editCustomerPricing({
          param: selectedPricing.customerPricingId,
          body: payload
        })
      ).then((res) => {
        if (res.error) {
          dispatch(
            openModal({
              type: "warning",
              message: res.error.message || t("PricingAlreadyExists")
            })
          );
        } else {
          dispatch(getCustomerPricings({ param: customerId }));
          dispatch(getCustomerServices({ param: customerId }));
          handleCloseDialog();
        }
      });
    }
  };

  const renderActionsCell = (cellData) => (
    <div className={classes.actionsCell}>
      <IconButton
        size="small"
        onClick={() => handleOpenEdit(cellData.data)}
        title={t("EditPricing")}
      >
        <img height={16} src={editIcon} alt={t("EditPricing")} />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleOpenDelete(cellData.data)}
        title={t("DeletePricing")}
      >
        <img height={16} src={deleteIcon} alt={t("DeletePricing")} />
      </IconButton>
    </div>
  );

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          {t("SpecialtiesAndTariffs")} - {serviceTypeName} ({currentBillingModel})
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          {t("AddPricing")}
        </Button>
      </div>

      <DataGrid
        dataSource={pricings}
        keyExpr="customerPricingId"
        showBorders={true}
        showRowLines={true}
        noDataText={t("NoSpecialtiesFoundForService")}
        allowColumnResizing={true}
        columnAutoWidth={true}
        width="100%"
        onContentReady={(e) => {
          e.component.updateDimensions();
        }}
      >
        <ColumnFixing enabled={true} />
        <Paging enabled={false} />
        <Sorting mode="single" />
        <LoadPanel enabled={loading === "loading"} />

        <Column
          dataField="specialtyName"
          caption={t("SpecialtyName")}
          width={180}
          fixed={true}
          fixedPosition="left"
          cssClass={classes.fixedColumn}
          cellRender={(cellData) => (
            <Tooltip title={cellData.value || ""} placement="top-start" enterDelay={300}>
              <span className={classes.specialtyCell}>
                {cellData.value}
              </span>
            </Tooltip>
          )}
        />
        <Column
          dataField="billingModel"
          caption={t("BillingModel")}
          minWidth={110}
        />
        <Column
          dataField="reportDescription"
          caption={t("ReportDescription")}
          minWidth={140}
        />
        <Column
          dataField="kmAllowance"
          caption={t("KmAllowance")}
          minWidth={100}
        />
        <Column
          dataField="baseRate"
          caption={t("BaseRate")}
          minWidth={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="excessKmRate"
          caption={t("ExcessKmRate")}
          minWidth={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="specialKmRate"
          caption={t("SpecialKmRate")}
          minWidth={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="dirtRoadKmRate"
          caption={t("DirtRoadKmRate")}
          minWidth={120}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="skatesRate"
          caption={t("SkatesRate")}
          minWidth={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="wheelRate"
          caption={t("WheelRate")}
          minWidth={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="stationDailyRate"
          caption={t("StationDailyRate")}
          minWidth={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="towingUpRate"
          caption={t("TowingUpRate")}
          minWidth={130}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="subwayRetentionRate"
          caption={t("SubwayRetentionRate")}
          minWidth={120}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="offroadRate"
          caption={t("OffroadRate")}
          minWidth={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="stopHourRate"
          caption={t("StopHourRate")}
          minWidth={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="workHourRate"
          caption={t("WorkHourRate")}
          minWidth={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="softwarePerPlate"
          caption={t("SoftwarePerPlate")}
          minWidth={130}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="toll"
          caption={t("Toll")}
          minWidth={120}
        />
        <Column
          dataField="concierge"
          caption={t("Concierge")}
          minWidth={120}
        />
        <Column
          dataField="goLive"
          caption={t("GoLive")}
          minWidth={120}
        />
        <Column
          dataField="observations"
          caption={t("Observations")}
          minWidth={160}
        />
        <Column
          dataField="closingObservations"
          caption={t("ClosingObservations")}
          minWidth={160}
        />
        <Column
          caption={t("Actions")}
          width={90}
          alignment="center"
          allowResizing={false}
          fixed={true}
          fixedPosition="right"
          cssClass={classes.fixedColumn}
          cellRender={renderActionsCell}
        />
      </DataGrid>

      {dialogOpen && operation !== "delete" && (
        <AdminDialog
          open={dialogOpen}
          title={
            operation === "create"
              ? `${t("AddPricing")} - ${serviceTypeName}`
              : `${t("EditPricing")} - ${serviceTypeName}`
          }
          onClose={handleCloseDialog}
          handleOnClick={handleSubmit}
          dialogSize={{ width: "700px", height: "auto" }}
        >
          <div className={classes.dialogContentContainer}>
            <div className={classes.formGrid}>
              <div className={classes.fullWidth}>
                <TextBox
                  label={t("SpecialtyName")}
                  labelMode="floating"
                  value={formData.specialtyName}
                  onValueChange={(val) => handleFieldChange("specialtyName", val)}
                />
              </div>
              <TextBox
                label={t("ReportDescription")}
                labelMode="floating"
                value={formData.reportDescription}
                onValueChange={(val) => handleFieldChange("reportDescription", val)}
              />
              <TextBox
                label={t("KmAllowance")}
                labelMode="floating"
                value={formData.kmAllowance}
                onValueChange={(val) => handleFieldChange("kmAllowance", val)}
              />
              <TextBox
                label={t("GoLive")}
                labelMode="floating"
                value={formData.goLive}
                onValueChange={(val) => handleFieldChange("goLive", val)}
              />
              <NumberBox
                label={t("BaseRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.baseRate}
                onValueChange={(val) => handleFieldChange("baseRate", val)}
              />
              <NumberBox
                label={t("ExcessKmRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.excessKmRate}
                onValueChange={(val) => handleFieldChange("excessKmRate", val)}
              />
              <NumberBox
                label={t("SpecialKmRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.specialKmRate}
                onValueChange={(val) => handleFieldChange("specialKmRate", val)}
              />
              <NumberBox
                label={t("DirtRoadKmRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.dirtRoadKmRate}
                onValueChange={(val) => handleFieldChange("dirtRoadKmRate", val)}
              />
              <NumberBox
                label={t("SkatesRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.skatesRate}
                onValueChange={(val) => handleFieldChange("skatesRate", val)}
              />
              <NumberBox
                label={t("WheelRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.wheelRate}
                onValueChange={(val) => handleFieldChange("wheelRate", val)}
              />
              <NumberBox
                label={t("StationDailyRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.stationDailyRate}
                onValueChange={(val) => handleFieldChange("stationDailyRate", val)}
              />
              <NumberBox
                label={t("TowingUpRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.towingUpRate}
                onValueChange={(val) => handleFieldChange("towingUpRate", val)}
              />
              <NumberBox
                label={t("SubwayRetentionRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.subwayRetentionRate}
                onValueChange={(val) => handleFieldChange("subwayRetentionRate", val)}
              />
              <NumberBox
                label={t("OffroadRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.offroadRate}
                onValueChange={(val) => handleFieldChange("offroadRate", val)}
              />
              <NumberBox
                label={t("StopHourRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.stopHourRate}
                onValueChange={(val) => handleFieldChange("stopHourRate", val)}
              />
              <NumberBox
                label={t("WorkHourRate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.workHourRate}
                onValueChange={(val) => handleFieldChange("workHourRate", val)}
              />
              <NumberBox
                label={t("SoftwarePerPlate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.softwarePerPlate}
                onValueChange={(val) => handleFieldChange("softwarePerPlate", val)}
              />
              <TextBox
                label={t("Toll")}
                labelMode="floating"
                value={formData.toll}
                onValueChange={(val) => handleFieldChange("toll", val)}
              />
              <TextBox
                label={t("Concierge")}
                labelMode="floating"
                value={formData.concierge}
                onValueChange={(val) => handleFieldChange("concierge", val)}
              />
              <div className={classes.fullWidth}>
                <TextArea
                  label={t("Observations")}
                  labelMode="floating"
                  height={80}
                  autoResizeEnabled={false}
                  value={formData.observations}
                  onValueChange={(val) => handleFieldChange("observations", val)}
                />
              </div>
              <div className={classes.fullWidth}>
                <TextArea
                  label={t("ClosingObservations")}
                  labelMode="floating"
                  height={80}
                  autoResizeEnabled={false}
                  value={formData.closingObservations}
                  onValueChange={(val) => handleFieldChange("closingObservations", val)}
                />
              </div>
            </div>
          </div>
        </AdminDialog>
      )}

      {dialogOpen && operation === "delete" && (
        <AdminDialog
          operation="delete"
          open={dialogOpen}
          title={t("DeletePricing")}
          onClose={handleCloseDialog}
          handleOnClick={handleSubmit}
        >
          <Typography className={classes.deleteConfirmText}>
            {t("DeleteCustomerPricingConfirm")}
          </Typography>
        </AdminDialog>
      )}
    </div>
  );
}

export default CustomerSpecialtiesDetail;
